import type { ReactNode } from "react";
import { ActivityIndicator, StyleSheet } from "react-native";
import { Dialog } from "react-native-paper";
import { Button } from "~/components/Button";
import { CentredModal, type CentredModalProps } from "~/components/layout/modal/CentredModal";
import { BodyText, Title } from "~/components/layout/text";
import type { QendoIconSet } from "~/components/QendoIcon";
import { Padding } from "~/constants/layout";
import { Space } from "~/constants/sizing";
import { Alignment } from "~/constants/typography";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";

declare type WithIcon = {
    icon: QendoIconSet;
    loading?: never;
};
declare type WithLoading = {
    loading: true;
    icon?: never;
    iconSize?: never;
    iconColour?: never;
};
declare type WithTitle = {
    title: string;
};
declare type WithDescription = {
    description: string;
};

export type AlertModalProps = {
    icon?: QendoIconSet;
    iconSize?: number;
    iconColour?: string;
    loading?: boolean;
    title?: string;
    description?: string;
    children?: ReactNode;
} & (WithLoading | WithIcon | WithTitle | WithDescription) &
    Omit<CentredModalProps, "children">;

export const AlertModal = ({
    icon,
    loading,
    title,
    description,
    children,
    style,
    iconSize = 38,
    iconColour,
    ...modalProps
}: AlertModalProps) => {
    const { colours } = useTheme();

    return (
        <CentredModal {...modalProps} style={[styles.body, style]}>
            {icon && <Dialog.Icon icon={icon} size={iconSize} color={iconColour ?? colours.icon.primary} />}
            {loading && <ActivityIndicator size="large" />}
            {title && (
                <Dialog.Title style={[Alignment.center, Padding.none]}>
                    <Title size="xl" align="center">
                        {title}
                    </Title>
                </Dialog.Title>
            )}
            {description && (
                <Dialog.Content style={[Padding.vertical_none]}>
                    <BodyText align="center">{description}</BodyText>
                </Dialog.Content>
            )}
            {filled(children)
                ? children
                : filled(modalProps.onDismiss) && (
                      <Dialog.Actions>
                          <Button title="Dismiss" size="sm" onPress={modalProps.onDismiss} />
                      </Dialog.Actions>
                  )}
        </CentredModal>
    );
};

const styles = StyleSheet.create({
    body: {
        alignItems: "center",
        gap: Space.none,
    },
});
